/**
 * MQTT SharedWorker Client
 * Client-side interface for connecting to MQTT via SharedWorker
 * This enables all tabs to share a single MQTT connection
 */

class MqttSharedWorkerClient {
  constructor() {
    this.worker = null
    this.isConnected = false
    this.messageHandlers = new Map()
    this.statusHandlers = new Set()
    this.eventHandlers = new Map()
    this.logoutHandlers = new Set()
    this.isEnableLogging = true
    this._beforeUnloadHandler = null
    this._logoutChannel = null
    this._hasProcessedLogout = false
  }

  /**
   * Log utility
   */
  log(message, type = 'log') {
    if (!this.isEnableLogging) return

    const timestamp = new Date().toISOString()
    const prefix = '[MQTT-Client]'
    const fullMessage = `${timestamp} ${prefix} ${message}`

    if (type === 'error') {
      console.error(fullMessage)
    } else if (type === 'warn') {
      console.warn(fullMessage)
    } else {
      console.log(fullMessage)
    }
  }

  /**
   * Initialize the SharedWorker connection
   */
  initialize(enableLogging = true) {
    if (this.worker) {
      this.log('SharedWorker already initialized', 'warn')
      return
    }

    this.isEnableLogging = enableLogging
    this._hasProcessedLogout = false

    try {
      this.worker = new SharedWorker('/mqtt-shared-worker.js', {
        name: 'qontak-mqtt-shared-worker',
        type: 'classic',
      })

      this.worker.port.onmessage = (event) => {
        this.handleWorkerMessage(event.data)
      }

      this.worker.port.onerror = (error) => {
        this.log(`SharedWorker error: ${error}`, 'error')
      }

      this.worker.port.start()

      // Notify worker when this tab unloads (best-effort — browser may throttle on teardown)
      this._beforeUnloadHandler = () => {
        try {
          this.worker.port.postMessage({ type: 'PORT_CLOSE', payload: {} })
        } catch (_) {
          // Swallow — tab is already closing
        }
      }
      window.addEventListener('beforeunload', this._beforeUnloadHandler)

      // Belt-and-suspenders BroadcastChannel receiver for tabs with stale ports
      if (typeof BroadcastChannel !== 'undefined') {
        try {
          this._logoutChannel = new BroadcastChannel('qontak_logout')
          this._logoutChannel.onmessage = (event) => {
            if (event.data?.type === 'LOGOUT') {
              this.log(
                'LOGOUT received via BroadcastChannel — triggering logout handlers'
              )
              this._handleLogout(event.data.payload)
            }
          }
        } catch (e) {
          this.log(`BroadcastChannel setup failed: ${e}`, 'warn')
        }
      }

      this.log('SharedWorker initialized')
    } catch (error) {
      this.log(`Failed to initialize SharedWorker: ${error}`, 'error')
      throw error
    }
  }

  /**
   * Handle messages from SharedWorker
   */
  handleWorkerMessage(data) {
    const { type, payload } = data

    this.log(`Received from worker: ${type}`)

    switch (type) {
      case 'CONNECTED':
        this.isConnected = true
        this.log('MQTT connected')
        this.notifyStatusHandlers({ connected: true })
        break

      case 'DISCONNECTED':
        this.isConnected = false
        this.log(`MQTT disconnected: ${payload.reason}`, 'warn')
        this.notifyStatusHandlers({ connected: false, reason: payload.reason })
        break

      case 'MESSAGE_RECEIVED':
        this.log(`Message received on topic: ${payload.topic}`)
        this.handleMqttMessage(payload)
        break

      case 'SUBSCRIBED':
        this.log(`Subscribed to topic: ${payload.topic}`)
        this.notifyEventHandlers('subscribed', payload)
        break

      case 'UNSUBSCRIBED':
        this.log(`Unsubscribed from topic: ${payload.topic}`)
        this.notifyEventHandlers('unsubscribed', payload)
        break

      case 'STATUS_UPDATE':
        this.isConnected = payload.connected
        this.notifyStatusHandlers(payload)
        break

      case 'ERROR':
        this.log(`MQTT Error [${payload.context}]: ${payload.error}`, 'error')
        this.notifyEventHandlers('error', payload)
        break

      case 'CONNECTED_TABS_COUNT':
        this.log(`Connected tabs: ${payload.count}`)
        this.notifyEventHandlers('tabsCount', payload)
        break

      case 'LOGOUT':
        this.log('LOGOUT received from worker — triggering logout handlers')
        this._handleLogout(payload)
        break

      default:
        this.log(`Unknown message type: ${type}`, 'warn')
    }
  }

  /**
   * Handle cross-tab logout — fires registered logout callbacks, then closes this port
   * @param {object} payload - Logout payload forwarded from the worker
   */
  _handleLogout(payload) {
    if (this._hasProcessedLogout) {
      this.log('LOGOUT already processed for this client instance', 'warn')
      return
    }

    this._hasProcessedLogout = true

    this.logoutHandlers.forEach((handler) => {
      try {
        handler(payload)
      } catch (error) {
        this.log(`Error in logout handler: ${error}`, 'error')
      }
    })

    // Notify the worker this port is now closing
    if (this.worker) {
      try {
        this.sendToWorker('PORT_CLOSE')
      } catch (_) {
        // Already shutting down — ignore
      }
    }

    // Close the port and reset the worker reference to support re-initialization
    try {
      this.worker?.port?.close()
    } catch (_) {}

    this.worker = null
    this.isConnected = false
  }

  /**
   * Handle MQTT messages and route to topic handlers
   */
  handleMqttMessage(payload) {
    const { topic, message, mqttEvent } = payload

    // Call handlers for this specific topic
    if (this.messageHandlers.has(topic)) {
      const handlers = this.messageHandlers.get(topic)
      handlers.forEach((handler) => {
        try {
          handler(mqttEvent, topic, message)
        } catch (error) {
          this.log(`Error in message handler for ${topic}: ${error}`, 'error')
        }
      })
    }

    // Call wildcard handlers (*)
    if (this.messageHandlers.has('*')) {
      const handlers = this.messageHandlers.get('*')
      handlers.forEach((handler) => {
        try {
          handler(mqttEvent, topic, message)
        } catch (error) {
          this.log(`Error in wildcard message handler: ${error}`, 'error')
        }
      })
    }
  }

  /**
   * Notify status change handlers
   */
  notifyStatusHandlers(status) {
    this.statusHandlers.forEach((handler) => {
      try {
        handler(status)
      } catch (error) {
        this.log(`Error in status handler: ${error}`, 'error')
      }
    })
  }

  /**
   * Notify event handlers
   */
  notifyEventHandlers(eventType, payload) {
    if (this.eventHandlers.has(eventType)) {
      const handlers = this.eventHandlers.get(eventType)
      handlers.forEach((handler) => {
        try {
          handler(payload)
        } catch (error) {
          this.log(`Error in event handler for ${eventType}: ${error}`, 'error')
        }
      })
    }
  }

  /**
   * Send message to SharedWorker
   */
  sendToWorker(type, payload = {}) {
    if (!this.worker) {
      this.log('SharedWorker not initialized', 'error')
      throw new Error('SharedWorker not initialized. Call initialize() first.')
    }

    this.worker.port.postMessage({ type, payload })
  }

  /**
   * Connect to MQTT broker
   */
  connect(brokerUrl, options = {}, enableLogging = true) {
    this.log(`Connecting to MQTT broker: ${brokerUrl}`)

    this.sendToWorker('CONNECT', {
      brokerUrl,
      options,
      enableLogging,
    })
  }

  /**
   * Disconnect from MQTT broker
   */
  disconnect() {
    this.log('Disconnecting from MQTT broker')
    this.sendToWorker('DISCONNECT')
  }

  /**
   * Signal a user logout to the SharedWorker.
   * Triggers MQTT disconnect + worker shutdown cascade across all tabs.
   * @param {object} payload - Optional metadata forwarded with the logout signal
   */
  logout(payload = {}) {
    if (this._hasProcessedLogout) {
      this.log('Logout already in progress or processed', 'warn')
      return
    }

    this.log('Sending LOGOUT to worker')
    this.sendToWorker('LOGOUT', payload)
  }

  /**
   * Register a logout handler — called when this tab receives a cross-tab logout signal
   * @param {function} handler - Callback function(payload)
   */
  onLogout(handler) {
    this.logoutHandlers.add(handler)
    this.log('Logout handler registered')
  }

  /**
   * Remove a logout handler
   * @param {function} handler - The handler to remove
   */
  offLogout(handler) {
    this.logoutHandlers.delete(handler)
    this.log('Logout handler removed')
  }

  /**
   * Subscribe to a topic
   */
  subscribe(topic, handler = null) {
    this.log(`Subscribing to topic: ${topic}`)

    // Add handler if provided
    if (handler) {
      this.on(topic, handler)
    }

    this.sendToWorker('SUBSCRIBE', { topic })
  }

  /**
   * Unsubscribe from a topic
   */
  unsubscribe(topic) {
    this.log(`Unsubscribing from topic: ${topic}`)

    // Remove handlers for this topic
    this.messageHandlers.delete(topic)

    this.sendToWorker('UNSUBSCRIBE', { topic })
  }

  /**
   * Publish message to a topic
   * @param {string} topic - Topic to publish to
   * @param {string|object} message - Message to publish
   * @param {object} options - Optional: { qos: 0|1|2 }
   * @param {function} callback - Optional: callback(success, error)
   */
  publish(topic, message, options = {}, callback = null) {
    this.log(`Publishing to topic: ${topic}`)

    const messageStr =
      typeof message === 'string' ? message : JSON.stringify(message)

    this.sendToWorker('PUBLISH', {
      topic,
      message: messageStr,
      options,
    })

    // // Since SharedWorker communication is async, we call callback immediately
    // // In a real implementation, you'd want to wait for acknowledgment from worker
    // if (callback && typeof callback === 'function') {
    //   // callback(success, error) - success=true, error=null for successful publish
    //   setTimeout(() => callback(true, null), 0)
    // }
  }

  /**
   * Register message handler for a topic
   * @param {string} topic - Topic to listen to, or '*' for all topics
   * @param {function} handler - Callback function(mqttEvent, topic, message)
   */
  on(topic, handler) {
    if (!this.messageHandlers.has(topic)) {
      this.messageHandlers.set(topic, new Set())
    }
    this.messageHandlers.get(topic).add(handler)
    this.log(`Handler registered for topic: ${topic}`)
  }

  /**
   * Remove message handler for a topic
   */
  off(topic, handler) {
    if (this.messageHandlers.has(topic)) {
      this.messageHandlers.get(topic).delete(handler)
      this.log(`Handler removed for topic: ${topic}`)
    }
  }

  /**
   * Register status change handler
   */
  onStatusChange(handler) {
    this.statusHandlers.add(handler)
    this.log('Status handler registered')
  }

  /**
   * Remove status change handler
   */
  offStatusChange(handler) {
    this.statusHandlers.delete(handler)
    this.log('Status handler removed')
  }

  /**
   * Register event handler
   * @param {string} eventType - 'subscribed', 'unsubscribed', 'error', 'tabsCount'
   */
  onEvent(eventType, handler) {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set())
    }
    this.eventHandlers.get(eventType).add(handler)
    this.log(`Event handler registered for: ${eventType}`)
  }

  /**
   * Remove event handler
   */
  offEvent(eventType, handler) {
    if (this.eventHandlers.has(eventType)) {
      this.eventHandlers.get(eventType).delete(handler)
      this.log(`Event handler removed for: ${eventType}`)
    }
  }

  /**
   * Get current connection status
   */
  getStatus() {
    this.sendToWorker('GET_STATUS')
  }

  /**
   * Get number of connected tabs
   */
  getConnectedTabsCount() {
    this.sendToWorker('GET_CONNECTED_TABS_COUNT')
  }

  /**
   * Refresh credentials (triggers reconnection with new credentials)
   */
  refreshCredentials() {
    this.log('Refreshing credentials')
    this.sendToWorker('REFRESH_CREDENTIALS')
  }

  /**
   * Check if connected
   */
  get connected() {
    return this.isConnected
  }

  /**
   * Cleanup - remove all handlers and browser-level listeners
   */
  cleanup() {
    this.log('Cleaning up handlers')
    this.messageHandlers.clear()
    this.statusHandlers.clear()
    this.eventHandlers.clear()
    this.logoutHandlers.clear()

    if (this._beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this._beforeUnloadHandler)
      this._beforeUnloadHandler = null
    }

    if (this._logoutChannel) {
      this._logoutChannel.close()
      this._logoutChannel = null
    }

    if (!this.worker) {
      this._hasProcessedLogout = false
    }
  }
}

// Create singleton instance
let mqttSharedWorkerClient = null

/**
 * Get or create MqttSharedWorkerClient instance
 */
export const getMqttSharedWorkerClient = () => {
  if (!mqttSharedWorkerClient) {
    mqttSharedWorkerClient = new MqttSharedWorkerClient()
  }
  return mqttSharedWorkerClient
}

/**
 * Initialize MQTT with SharedWorker
 */
export const initializeMqttSharedWorker = (
  brokerUrl,
  options = {},
  enableLogging = true
) => {
  const client = getMqttSharedWorkerClient()

  // Cleanup existing handlers before re-initialization to prevent accumulation
  client.cleanup()

  // Initialize SharedWorker if not already done
  if (!client.worker) {
    client.initialize(enableLogging)
  }

  // Connect to MQTT broker
  client.connect(brokerUrl, options, enableLogging)

  return client
}

/**
 * Disconnect MQTT SharedWorker
 */
export const disconnectMqttSharedWorker = () => {
  const client = getMqttSharedWorkerClient()
  client.disconnect()
}

/**
 * Signal a user logout to the SharedWorker.
 * Triggers MQTT disconnect + worker shutdown cascade across all tabs.
 * @param {object} payload - Optional metadata forwarded with the logout signal
 */
export const logoutMqttSharedWorker = (payload = {}) => {
  getMqttSharedWorkerClient().logout(payload)
}

export default {
  getMqttSharedWorkerClient,
  initializeMqttSharedWorker,
  disconnectMqttSharedWorker,
  logoutMqttSharedWorker,
}
